/**
 * Battery Alert Sync Service (Global Battery Alert)
 * 
 * Uses alertType: "battery" to create a single global alert that monitors
 * ALL battery devices automatically on the server side.
 * 
 * - syncBatteryAlerts(): Create/update/delete global battery alert
 * - getBatteryAlerts(): Get all existing battery alerts from server
 * - getGlobalBatteryAlert(): Find the global battery alert (if exists)
 */

import { logger } from '../utils/logger';
import { ultronTuringApi } from './ultronTuringApi';

export interface BatteryAlert {
  id: string;
  name: string;
  device?: string;  // Optional for global alerts
  metric?: string;
  condition?: string;
  threshold: number;
  enabled: boolean;
  severity?: string;
  triggerMode?: string;
  alertType?: string;  // 'standard' | 'battery' (input format)
  type?: string;  // 'battery_alert' | 'standard_alert' (server response format)
}

export interface SyncResult {
  synced: number;
  failed: number;
  errors: string[];
}

// Global battery alert name
const GLOBAL_BATTERY_ALERT_NAME = '電池電量警報';

/**
 * Get all battery alerts from Turing Server
 * @returns Array of battery alerts
 */
export async function getBatteryAlerts(): Promise<BatteryAlert[]> {
  try {
    logger.log('🔋 [BatteryAlertService] Fetching battery alerts from server...');
    const response: any = await ultronTuringApi.queryAlerts();
    
    // Extract alerts from response
    const responseData = response?.data || response || {};
    const allAlerts: any[] = responseData?.alerts || [];
    
    // Filter for battery alerts: type === 'battery_alert' OR alertType === 'battery' OR metric === 'battery'
    // Note: Server returns type: 'battery_alert' for global battery alerts (not alertType)
    const batteryAlerts = allAlerts.filter((alert: any) => 
      alert.type === 'battery_alert' || alert.alertType === 'battery' || alert.metric === 'battery'
    );
    
    logger.log(`🔋 [BatteryAlertService] Found ${batteryAlerts.length} battery alerts`);
    
    return batteryAlerts.map((alert: any) => {
      // Check enabled status: root level OR inside schedule object
      // Firestore may store enabled in schedule.enabled for battery alerts
      const isEnabled = alert.enabled !== undefined 
        ? alert.enabled === true 
        : (alert.schedule?.enabled !== false);  // Default to true if not explicitly false
      
      logger.log(`🔋 [BatteryAlertService] Mapping alert ${alert.id}: enabled=${alert.enabled}, schedule.enabled=${alert.schedule?.enabled}, result=${isEnabled}`);
      
      return {
        id: alert.id,
        name: alert.name,
        device: alert.device || alert.deviceSN,
        metric: alert.metric,
        condition: alert.condition,
        threshold: alert.threshold || alert.conditions?.threshold,  // Server may return threshold in conditions
        enabled: isEnabled,
        severity: alert.severity,
        triggerMode: alert.triggerMode,
        alertType: alert.alertType,
        type: alert.type  // Server response format: 'battery_alert'
      };
    });
  } catch (error) {
    logger.error('🔋 [BatteryAlertService] Failed to fetch battery alerts:', error);
    return [];
  }
}

/**
 * Get the global battery alert (alertType: 'battery') if it exists
 * @returns The global battery alert or null
 */
export async function getGlobalBatteryAlert(): Promise<BatteryAlert | null> {
  const alerts = await getBatteryAlerts();
  
  // Find global battery alert by type, alertType, or by name
  // Note: Server returns type: 'battery_alert' for global battery alerts
  const globalAlert = alerts.find(a => 
    a.type === 'battery_alert' || a.alertType === 'battery' || a.name === GLOBAL_BATTERY_ALERT_NAME
  );
  
  if (globalAlert) {
    logger.log(`🔋 [BatteryAlertService] Found global battery alert: ${globalAlert.id}`);
  } else {
    logger.log('🔋 [BatteryAlertService] No global battery alert found');
  }
  
  return globalAlert || null;
}

/**
 * Sync global battery alert to Turing Server
 * When enabled=true: Creates or updates a single global battery alert
 * When enabled=false: Deletes the global battery alert
 * 
 * The server automatically monitors ALL devices with battery capability.
 * 
 * @param enabled - Whether to enable or disable battery alerts
 * @param threshold - Battery level threshold for alerts (e.g., 20 for 20%)
 * @param notificationChannels - Notification channels (websocket, email)
 * @param email - Email address for notifications (optional, uses account email if not provided)
 * @returns Sync result with counts
 */
/**
 * Sync battery alert settings to server
 * NOTE: Notification channels are now managed via Notification Center (QueryNotifications set_preferences)
 * @param enabled - Whether the battery alert is enabled
 * @param threshold - Battery percentage threshold
 * @param email - Optional custom email address for notifications
 */
export async function syncBatteryAlerts(
  enabled: boolean,
  threshold: number,
  email?: string
): Promise<SyncResult> {
  const result: SyncResult = {
    synced: 0,
    failed: 0,
    errors: []
  };

  try {
    // Step 1: Check for existing global battery alert
    const existingAlert = await getGlobalBatteryAlert();
    logger.log(`🔋 [BatteryAlertService] Existing global alert: ${existingAlert?.id || 'none'}`);

    if (enabled) {
      // Create or update global battery alert (enabled)
      // Note: HTTP API may not support alertType:'battery' for create
      // If create fails, user needs to enable via Turing AI Chat first
      try {
        const action = existingAlert ? 'update' : 'create';
        
        // Use configureGlobalBatteryAlert for the correct format
        // alertType: 'battery' routes to handleConfigureBatteryAlert on server
        // Note: Notification channels are managed via Notification Center (QueryNotifications set_preferences)
        
        // Debug logging for email
        logger.log(`🔋 [BatteryAlertService] Sending email to server: "${email}"`);
        
        await ultronTuringApi.configureGlobalBatteryAlert({
          action,
          id: existingAlert?.id,
          name: GLOBAL_BATTERY_ALERT_NAME,
          threshold: threshold,
          enabled: true,  // Explicitly enable
          email: email,  // Custom email or undefined (server uses account email)
          schedule: {
            time: '09:00',
            timezone: 'Asia/Taipei'
          }
        });

        result.synced = 1;
        logger.log(`🔋 [BatteryAlertService] Global battery alert ${action}d and enabled successfully`);
      } catch (error) {
        result.failed = 1;
        const errorMsg = error instanceof Error ? error.message : String(error);
        // Add helpful hint for create failures
        if (!existingAlert) {
          result.errors.push('無法建立電池警報。請先使用 Turing AI 助手啟用電池警報功能。');
        } else {
          result.errors.push(errorMsg);
        }
        logger.error('🔋 [BatteryAlertService] Failed to configure global battery alert:', error);
      }
    } else {
      // Delete global battery alert if it exists
      // Note: HTTP API doesn't support update with enabled:false for battery alerts
      // So we delete instead of disabling
      if (existingAlert) {
        try {
          logger.log(`🔋 [BatteryAlertService] Deleting global battery alert: ${existingAlert.id}`);
          
          await ultronTuringApi.removeConfiguration('alert', existingAlert.id);

          result.synced = 1;
          logger.log('🔋 [BatteryAlertService] Global battery alert deleted successfully');
        } catch (error) {
          result.failed = 1;
          const errorMsg = error instanceof Error ? error.message : String(error);
          result.errors.push(errorMsg);
          logger.error('🔋 [BatteryAlertService] Failed to delete global battery alert:', error);
        }
      } else {
        // No existing alert - nothing to delete, count as success
        logger.log('🔋 [BatteryAlertService] No global battery alert to delete');
        result.synced = 1;
      }
    }

    logger.log(`🔋 [BatteryAlertService] Sync complete: ${result.synced} synced, ${result.failed} failed`);
    return result;
  } catch (error) {
    logger.error('🔋 [BatteryAlertService] Sync failed:', error);
    throw error;
  }
}
